import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Map from "@arcgis/core/Map";
import UniqueValueRenderer from "@arcgis/core/renderers/UniqueValueRenderer";
import MapView from "@arcgis/core/views/MapView";
import Popup from "@arcgis/core/widgets/Popup";
import QueryTask from "@arcgis/core/tasks/QueryTask";
import Query from "@arcgis/core/rest/support/Query";
import Extent from "@arcgis/core/geometry/Extent";
import { Action, Dispatch } from "@reduxjs/toolkit";
import store, { AppDispatch } from "../../state-management/store";
import {
  EMarkerStatuses,
  MIN_SCALE,
  OUT_FIELDS,
  OVERLAY_LAYER_ID,
  PIN_MODE_LAYER_ID,
} from "./Map.constants";
import {
  FeatureSet,
  IPolyFill,
  IStreetlightFields,
  ISymbol,
  SpatialReference,
} from "./Map.types";
import { LocationWidget } from "./widgets/LocationWidget";
import StreetLightMarker from "../../theme/assets/map-markers/dot_streetlight.svg";
import ReportedStreetLightMarker from "../../theme/assets/map-markers/dot_reported.svg";
import ExtendedRepairMarker from "../../theme/assets/map-markers/dot_extended-repair.svg";
import NonCEMarker from "../../theme/assets/map-markers/dot_non-ce.svg";
import { OutageViewModel } from "../../api/generated/models";

import * as watchUtils from "@arcgis/core/core/watchUtils";
import { IAppConfig } from "../../shared/types/config.types";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import MapUtils from "./Map.utils";
import PinModeMarker from "../../theme/assets/pin-mode-marker.svg";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import {
  setFeatureLayerInitialized,
  setOutageNotFound,
  updatePin,
  setRoutingOutage,
  setRoutingRoutineComplete,
  updateMarkerData,
  setRestrictedLayerInitialized,
  setGrandRapidsLayerInitialized,
  setSaginawLayerInitialized,
  setJacksonLayerInitialized,
} from "../../state-management/slices/global/global.slice";
import Point from "@arcgis/core/geometry/Point";
import { ApiService } from "../../api/api.service";
import { isNullOrUndefined } from "../../shared/utils/data.utils";
import { ECompletedReasons } from "../../shared/enums/outage.enums";

export class FeatureLayerService {
  private static instance: FeatureLayerService;
  private dispatch: AppDispatch;
  private mapView: MapView;
  private locationWidget: LocationWidget;
  private pinModeLayer!: FeatureLayer;
  private streetlightLayer!: FeatureLayer;
  private restrictedLayer!: FeatureLayer;
  private grandRapidsLayer!: FeatureLayer;
  private saginawLayer!: FeatureLayer;
  private jacksonLayer!: FeatureLayer;
  private streetlightFields!: IStreetlightFields;
  private streetLightRenderer!: UniqueValueRenderer;
  private pinModeRenderer!: SimpleRenderer;
  private grandRapidsRenderer!: SimpleRenderer;
  private saginawRenderer!: SimpleRenderer;
  private jacksonRenderer!: SimpleRenderer;
  private streetlightSpatialReference!: SpatialReference;
  private overlayLayer!: GraphicsLayer;
  private popup!: Popup;
  private map!: Map;
  private streetlightSymbol!: ISymbol;
  private reportedSymbol!: ISymbol;
  private extendedRepairSymbol!: ISymbol;
  private nonCESymbol!: ISymbol;
  private pinModeSymbol!: ISymbol;
  private overlayFill!: IPolyFill;
  private featureLayerURL: string;
  private featureLayerID: string;
  private restrictedLayerURL: string;
  private grandRapidsLayerID: string;
  private grandRapidsLayerURL: string;
  private saginawLayerId: string;
  private saginlayerURL: string;
  private jacksonLayerId: string;
  private jacksonlayerURL: string;
  private restrictedLayerID: string;
  private appConfig: IAppConfig;
  private queryTask: QueryTask;
  private APIService: ApiService;

  /**
   * Creates an instance of FeatureLayerService.
   * @param {MapView} mapView
   * @param {AppDispatch} dispatch
   * @memberof FeatureLayerService
   */
  private constructor(
    map: Map,
    mapView: MapView,
    dispatch: AppDispatch,
    appConfig: IAppConfig,
    locationWidget: LocationWidget
  ) {
    this.map = map;
    this.mapView = mapView;
    this.dispatch = dispatch;
    this.APIService = ApiService.getInstance();

    this.appConfig = appConfig;
    this.featureLayerID = this.appConfig.esriStreetlightLayerId;
    this.restrictedLayerID = this.appConfig.esriRestrictedLayerId;
    this.grandRapidsLayerID = this.appConfig.esriGrandRapidsLayerId;
    this.saginawLayerId = this.appConfig.esriSaginawLayerId;
    this.jacksonLayerId = this.appConfig.esriJacksonLayerId;
    this.restrictedLayerURL = this.appConfig.esriRestrictedFeatureLayerUrl;
    this.grandRapidsLayerURL = this.appConfig.esriGrandRapidsLayerUrl;
    this.saginlayerURL = this.appConfig.esriSaginawLayerUrl;
    this.jacksonlayerURL = this.appConfig.esriJacksonLayerUrl;
    this.featureLayerURL = this.appConfig.esriFeatureLayerUrl;
    this.locationWidget = locationWidget;

    this.queryTask = new QueryTask({ url: `${this.featureLayerURL}` });

    this.buildPopup();
    this.buildSymbolMarkers();
    this.buildStreetlightRenderer();
    this.buildGrandRapidsRenderer();
    this.buildSaginawRenderer();
    this.buildJacksonRenderer();
    this.buildPinModeRenderer();
  }

  /**
   * @private buildPopup
   * @memberof FeatureLayerService
   */
  private buildPopup(): void {
    this.popup = new Popup({
      alignment: "auto",
      autoCloseEnabled: false,
      spinnerEnabled: false,
      id: "DefaultPopup",
    });
    this.mapView.popup = this.popup;
  }

  /**
   * @private buildSymbolMarkers
   * @memberof FeatureLayerService
   */
  private buildSymbolMarkers(): void {
    this.streetlightSymbol = {
      type: "picture-marker",
      url: StreetLightMarker,
      height: 12,
      width: 12,
    };

    this.reportedSymbol = {
      type: "picture-marker",
      url: ReportedStreetLightMarker,
      height: 12,
      width: 12,
    };

    this.extendedRepairSymbol = {
      type: "picture-marker",
      url: ExtendedRepairMarker,
      height: 12,
      width: 12,
    };

    this.nonCESymbol = {
      type: "picture-marker",
      url: NonCEMarker,
      height: 12,
      width: 12,
    };

    this.overlayFill = {
      type: "simple-fill",
      color: [0, 0, 0, 0.25],
      outline: null,
    };

    this.pinModeSymbol = {
      type: "picture-marker",
      url: PinModeMarker,
      height: 30,
      width: 21,
    };
  }

  /**
   * @private buildStreetlightRenderer
   * @memberof FeatureLayerService
   */
  private buildStreetlightRenderer(): void {
    this.streetLightRenderer = new UniqueValueRenderer({
      field: "markerStatus",
      defaultSymbol: this.streetlightSymbol,
      uniqueValueInfos: [
        {
          value: EMarkerStatuses.COMPLETED,
          symbol: this.streetlightSymbol,
        },
        {
          value: EMarkerStatuses.REPORTED,
          symbol: this.reportedSymbol,
        },
        {
          value: EMarkerStatuses.IN_PROGRESS,
          symbol: this.reportedSymbol,
        },
        {
          value: EMarkerStatuses.EXTENDED_REPAIR,
          symbol: this.extendedRepairSymbol,
        },
        {
          value: EMarkerStatuses.NON_CE,
          symbol: this.nonCESymbol,
        },
      ],
    });
  }

  /**
   * @private buildPinModeRenderer
   * @memberof FeatureLayerService
   */
  private buildPinModeRenderer(): void {
    this.pinModeRenderer = new SimpleRenderer({
      symbol: this.pinModeSymbol,
    });
  }

  /**
   * @private buildGrandRapidsRenderer
   * @memberof FeatureLayerService
   */
  private buildGrandRapidsRenderer(): void {
    this.grandRapidsRenderer = new SimpleRenderer({
      symbol: this.nonCESymbol,
    });
  }

  /**
   * @private buildSaginawRenderer
   * @memberof FeatureLayerService
   */
  private buildSaginawRenderer(): void {
    this.saginawRenderer = new SimpleRenderer({
      symbol: this.nonCESymbol,
    });
  }

  /**
   * @private buildJacksonRenderer
   * @memberof FeatureLayerService
   */
  private buildJacksonRenderer(): void {
    this.jacksonRenderer = new SimpleRenderer({
      symbol: this.nonCESymbol,
    });
  }

  /**
   * buildOverlayLayer
   * @memberof FeatureLayerService
   */
  public buildOverlayLayer(): void {
    if (this.overlayLayer) {
      this.map.remove(this.overlayLayer);
    }

    const polygonGraphic = new Graphic({
      geometry: {
        type: "polygon",
        rings: [
          [-125.59570313, 49.66762782],
          [-65.04318237, 49.00634835],
          [-65.53001404, 24.50027046],
          [-123.92990112, 24.50027046],
        ],
      } as any,
      symbol: this.overlayFill,
    });

    this.overlayLayer = new GraphicsLayer({
      id: OVERLAY_LAYER_ID,
      graphics: [polygonGraphic],
      opacity: 0.0,
    });
  }

  /**
   * addOverlayLayer
   * @memberof FeatureLayerService
   */
  public addOverlayLayer(): void {
    this.map.add(this.overlayLayer, 1);
  }

  /**
   * removeOverlayLayer
   * @memberof FeatureLayerService
   */
  public removeOverlayLayer(): void {
    this.map.remove(this.overlayLayer);
  }

  /**
   * removePinModeGraphics
   * @memberof FeatureLayerService
   */
  public removePinModeGraphics = (): void => {
    this.pinModeLayer
      .queryFeatures(this.pinModeLayer.createQuery())
      .then((results) => {
        if (results.features.length) {
          this.pinModeLayer.applyEdits({
            deleteFeatures: results.features,
          });
        }
      });
  };

  /**
   * removeNonCEGraphics
   * @memberof FeatureLayerService
   */
  public removeNonCEGraphics = (): void => {
    this.streetlightLayer
      .queryFeatures(this.streetlightLayer.createQuery())
      .then((results) => {
        const nonCELights: Graphic[] = [];
        if (results.features.length) {
          results.features.forEach((marker) => {
            const { markerStatus, GISID } = marker.attributes;
            if (
              markerStatus === EMarkerStatuses.DOES_NOT_EXIST ||
              isNullOrUndefined(GISID)
            ) {
              nonCELights.push(marker);
            }
          });
        }

        if (nonCELights.length) {
          this.streetlightLayer.applyEdits({
            deleteFeatures: nonCELights,
          });
        }
      });
  };

  /**
   * removeMapViewGraphics
   * @memberof FeatureLayerService
   */
  public removeMapViewGraphics = (): void => {
    this.mapView.graphics.removeAll();
  };

  /**
   * fadeOverlay
   * @param {("on" | "off")} opacity
   * @memberof FeatureLayerService
   */
  public fadeOverlay = (opacity: "on" | "off"): void => {
    const overlay = this.overlayLayer;
    const finalOpacity = opacity === "on" ? 1 : 0;
    const mapInstance = this.map;
    let animating = true;
    let currentOpacity = opacity === "on" ? 0 : 1;

    overlay.opacity = currentOpacity;
    function incrementOpacityByFrame() {
      if (opacity === "on") {
        if (currentOpacity >= finalOpacity && animating) {
          animating = false;
          return;
        }
      } else {
        if (currentOpacity <= finalOpacity && animating) {
          animating = false;
          mapInstance.remove(overlay);
          return;
        }
      }

      if (opacity === "on") {
        currentOpacity += 0.05;
      } else {
        currentOpacity -= 0.05;
      }

      overlay.opacity = currentOpacity;

      // Continue the animation at the next frame
      requestAnimationFrame(incrementOpacityByFrame);
    }

    this.mapView.whenLayerView(overlay).then((layerView) => {
      watchUtils.whenFalseOnce(layerView, "updating", (updating) => {
        requestAnimationFrame(incrementOpacityByFrame);
      });
    });
  };

  /**
   * @private buildRestrictedLayer
   * @memberof FeatureLayerService
   */
  private buildRestrictedLayer(): void {
    this.restrictedLayer = new FeatureLayer({
      id: this.restrictedLayerID,
      url: this.restrictedLayerURL,
      opacity: 0.2,
    });

    this.map.add(this.restrictedLayer, 1);
    this.dispatch(setRestrictedLayerInitialized(true));
    this.buildGrandRapidsLayer();
    this.buildSaginawLayer();
    this.buildJacksonLayer();
  }

  private buildGrandRapidsLayer(): void {
    this.grandRapidsLayer = new FeatureLayer({
      id: this.grandRapidsLayerID,
      outFields: OUT_FIELDS,
      minScale: MIN_SCALE,
      renderer: this.grandRapidsRenderer,
      url: this.grandRapidsLayerURL,
      geometryType: "point",
      popupTemplate: {
        actions: [],
        content: MapUtils.getGrandRapidsPopupContent(
          this.dispatch,
          this.mapView,
          this.popup,
          this.grandRapidsLayerID
        ),
      },
    });

    this.map.add(this.grandRapidsLayer, 2);
    this.dispatch(setGrandRapidsLayerInitialized(true));
  }

  private buildSaginawLayer(): void {
    this.saginawLayer = new FeatureLayer({
      id: this.saginawLayerId,
      outFields: OUT_FIELDS,
      minScale: MIN_SCALE,
      renderer: this.saginawRenderer,
      url: this.saginlayerURL,
      geometryType: "point",
      popupTemplate: {
        actions: [],
        content: MapUtils.getGrandRapidsPopupContent(
          this.dispatch,
          this.mapView,
          this.popup,
          this.saginawLayerId
        ),
      },
    });

    this.map.add(this.saginawLayer, 2);
    this.dispatch(setSaginawLayerInitialized(true));
  }

  private buildJacksonLayer(): void {
    this.jacksonLayer = new FeatureLayer({
      id: this.jacksonLayerId,
      outFields: OUT_FIELDS,
      minScale: MIN_SCALE,
      renderer: this.jacksonRenderer,
      url: this.jacksonlayerURL,
      geometryType: "point",
      popupTemplate: {
        actions: [],
        content: MapUtils.getGrandRapidsPopupContent(
          this.dispatch,
          this.mapView,
          this.popup,
          this.jacksonLayerId
        ),
      },
    });

    this.map.add(this.jacksonLayer, 2);
    this.dispatch(setJacksonLayerInitialized(true));
  }

  /**
   * buildFeatureLayer
   * @private
   * @memberof FeatureLayerService
   */
  private buildFeatureLayer(): void {
    const currentState = store.getState();
    const { routing } = currentState.globalState.ui;
    const { outageCollection } = currentState.outages;

    this.streetlightLayer = new FeatureLayer({
      id: this.featureLayerID,
      outFields: OUT_FIELDS,
      minScale: MIN_SCALE,
      fields: this.streetlightFields,
      renderer: this.streetLightRenderer,
      spatialReference: this.streetlightSpatialReference,
      geometryType: "point",
      popupTemplate: {
        actions: [],
        content: MapUtils.getPopupContent(
          this.dispatch,
          this.mapView,
          this.popup,
          this.featureLayerID
        ),
      },
    });

    this.streetlightLayer.source = [] as any;
    this.map.add(this.streetlightLayer, 0);

    this.dispatch(setFeatureLayerInitialized(true));
    this.buildRestrictedLayer();

    if (
      !isNullOrUndefined(routing.outageId) &&
      !routing.routeNavigationComplete
    ) {
      const { outagesApi } = this.APIService;
      const outageId = parseInt(routing.outageId);
      let routeOutage: any = null;

      outagesApi
        .getOutageById(outageId)
        .then((outage: OutageViewModel) => {
          if (outage && outage.location && outage.location.coordinates) {
            routeOutage = outage;
          }
        })
        .finally(() => {
          if (routeOutage) {
            this.centerMapToLocation(routeOutage);
          } else {
            this.dispatch(setOutageNotFound(true));
            this.locationWidget.getUserLocation();
          }
        });
    } else {
      this.locationWidget.getUserLocation();
    }
  }

  /**
   * @private buildPinModeLayer
   * @memberof FeatureLayerService
   */
  public async buildPinModeLayer(): Promise<void> {
    this.pinModeLayer = new FeatureLayer({
      id: PIN_MODE_LAYER_ID,
      outFields: OUT_FIELDS,
      minScale: MIN_SCALE,
      fields: this.streetlightFields,
      renderer: this.pinModeRenderer,
      spatialReference: this.streetlightSpatialReference,
      geometryType: "point",
      popupTemplate: {
        actions: [],
        content: MapUtils.getPopupContent(
          this.dispatch,
          this.mapView,
          this.popup,
          this.featureLayerID
        ),
      },
    });

    this.pinModeLayer.source = [] as any;
    this.map.add(this.pinModeLayer, 2);
  }

  /**
   * queryFeatureLayer
   * @param {Extent} currentExtent
   * @return {*}  {Promise<any>}
   * @memberof FeatureLayerService
   *
   * @note First, we query the feature layer once so that we
   * can THEN get the fields and spatialReference from the featureLayer and store them.
   * FINALLY, we build the feature layer
   */
  public async queryFeatureLayer(currentExtent: Extent): Promise<any> {
    const params = new Query({
      geometry: currentExtent,
      returnGeometry: true,
      returnExceededLimitFeatures: true,
      orderByFields: ["OBJECTID"],
      num: 1,
      outFields: ["*"],
      where: "1 = 1",
    });

    await this.queryTask
      .execute(params)
      .then((featureSet: FeatureSet) => {
        const { fields, spatialReference } = featureSet;
        this.streetlightSpatialReference = spatialReference;
        this.streetlightFields = [
          ...fields,
          {
            name: "markerStatus",
            type: "string",
          },
        ];
      })
      .catch((error) => {
        if (error) {
          console.log(error);
        }
        return Promise.reject();
      })
      .finally(() => {
        this.buildFeatureLayer();
        return Promise.resolve();
      });
  }

  public centerMapToLocation(outage: OutageViewModel): void {
    const latitude = outage.location?.coordinates?.latitude;
    const longitude = outage.location?.coordinates?.longitude;

    if (!latitude || !longitude) {
      return;
    }

    this.mapView
      .goTo({
        center: [longitude, latitude],
        scale: 1000,
      })
      .finally(async () => {
        this.dispatch(setRoutingOutage(outage));
      });
  }

  private renderRoutingPopup = async (
    gisOutageData: any,
    outageData: OutageViewModel
  ): Promise<void> => {
    const { attributes } = gisOutageData;

    const mapPoint = new Point({
      latitude: attributes.LATITUDE,
      longitude: attributes.LONGITUDE,
    });

    const geoLocResponse = await MapUtils.fetchGeoLocData(mapPoint);
    const xStreetsResponse = await MapUtils.fetchCrossStreets(mapPoint);
    const { streetAddress, geoLocPayload } = MapUtils.buildGeoLocPayload(
      geoLocResponse,
      xStreetsResponse
    );

    this.mapView.popup.open({
      location: mapPoint,
      content: MapUtils.getRoutingPopupContent(
        this.mapView,
        streetAddress,
        outageData,
        this.removeMapViewGraphics
      ) as any,
    });
  };

  /**
   * @private updateFeatureLayer
   * @param {Array<any>} data
   * @memberof FeatureLayerService
   */
  private updateFeatureLayer = async (
    data: Array<any>,
    outages: Array<OutageViewModel>
  ): Promise<any> => {
    // If we dont have data... reject and return
    if ((!data || !data.length) && (!outages || !outages.length))
      return Promise.reject();

    let droppedPins = null;
    let updates = null;
    let droppedPinUpdates = null;
    let features: any[] | null = null;

    /**
     * IF we have outages in azure, we filter out any that are dropped pins.
     * THEN, IF we have dropped pins, we create a new Graphic object for each one of them,
     * and create a new ARRAY out of them
     *
     * @todo: Consider creating a seperate utility for this
     * @let droppedPinUpdates
     */
    if (outages.length) {
      droppedPins = outages.filter((outage: OutageViewModel) => {
        const { completedReason } = outage;

        return (
          isNullOrUndefined(outage.location?.locationId) &&
          (completedReason === ECompletedReasons.NON_CE ||
            isNullOrUndefined(completedReason))
        );
      });

      if (droppedPins.length) {
        droppedPinUpdates = droppedPins.map((pin) => {
          return new Graphic({
            geometry: new Point({
              longitude: pin.location?.coordinates?.longitude,
              latitude: pin.location?.coordinates?.latitude,
            }),
            attributes: {
              LATITUDE: pin.location?.coordinates?.latitude,
              LONGITUDE: pin.location?.coordinates?.longitude,
              LAMPTYPE: pin.utility?.lampType,
              FIXTURESUP: pin.utility?.bracketType,
              markerStatus: pin.markerStatus,
            },
            symbol: this.reportedSymbol,
          });
        });
      }
    }

    /**
     * We map over all of our data from GIS.
     * FOR EACH item in GIS data, WE map over azure data and check
     * for a match based on LOCATION ID(Azure) and GISID(GIS).
     * We build a new Graphic object for each record,
     * adding markerStatus attribute for the ones that are a match.
     *
     * @constant updates
     */

    if (data && data.length) {
      updates = data.map((feature: any) => {
        const { geometry, attributes } = feature;
        let markerStatus: EMarkerStatuses = EMarkerStatuses.COMPLETED;
        if (outages.length) {
          outages.forEach((outage: OutageViewModel) => {
            if (outage.location?.locationId === attributes.GISID) {
              // eslint-disable-next-line prefer-destructuring
              markerStatus = outage.markerStatus as EMarkerStatuses;
            }
          });
        }

        return new Graphic({
          geometry,
          attributes: {
            ...attributes,
            markerStatus,
          },
        });
      });
    }

    // IF we have dropped pin markers, we merge those into our
    // updates ARRAY that was built above. Otherwise, we just use
    // the updates ARRAY. If we've made it here and we have no data,
    // Reject the promise and return.
    if (droppedPinUpdates?.length) {
      if (updates?.length) {
        features = updates.concat(droppedPinUpdates);
      } else {
        features = droppedPinUpdates;
      }
    } else {
      if (updates?.length) {
        features = updates;
      } else {
        return Promise.reject();
      }
    }

    // APPLY Updates to map feature layer.
    return this.streetlightLayer
      .applyEdits({
        addFeatures: [...features],
      })
      .finally(() => {
        // FINALLY, once our feature layer was updated, we check to
        // see if we are in our ROUTING ROUTINE, meaning, if we are
        // currently routing by URL Parameter. IF we are, we select
        // that marker, and open it's popup.
        const { ui } = store.getState().globalState;
        const {
          routeNavigationComplete,
          selectedOutage,
          routingRoutineComplete,
        } = ui.routing;

        if (
          routeNavigationComplete &&
          selectedOutage &&
          !routingRoutineComplete
        ) {
          let gisOutageData = null;

          if (features && features.length) {
            gisOutageData = features.find(
              (gisOutage: any, index: number, obj: any[]) => {
                const { attributes } = gisOutage;
                const { location } = selectedOutage;

                /**
                 * If we do not have a locationID, that means it's
                 * a dropped pin so we need to compare latitude and longitude
                 */
                if (location && !location.locationId) {
                  if (
                    location?.coordinates?.latitude === attributes.LATITUDE &&
                    location?.coordinates?.longitude === attributes.LONGITUDE
                  ) {
                    return gisOutage;
                  }
                }
                /**
                 * If we have a locationID, then we compare the locationId to
                 * GISID for a match
                 */
                if (
                  location &&
                  location?.locationId &&
                  attributes.GISID === location?.locationId
                ) {
                  return gisOutage;
                }
              }
            );
          }

          if (gisOutageData) {
            const { attributes } = gisOutageData;
            gisOutageData.symbol = MapUtils.getMarkerByStatus(
              attributes.markerStatus,
              true
            );

            this.removeMapViewGraphics();
            this.mapView.graphics.add(gisOutageData);
            this.dispatch(
              updateMarkerData({
                attributes: gisOutageData.attributes,
              })
            );

            this.renderRoutingPopup(gisOutageData, selectedOutage);
            this.dispatch(setRoutingRoutineComplete(true));
          }
        }
      });
  };

  /**
   * addMarker
   * @param {__esri.Point} mapPoint
   * @memberof FeatureLayerService
   */
  public addMarker = async (mapPoint: __esri.Point): Promise<void> => {
    const attributes = {
      LATITUDE: mapPoint.latitude,
      LONGITUDE: mapPoint.longitude,
      markerStatus: "",
    };

    const markerData = {
      attributes,
    };

    await this.pinModeLayer
      .queryFeatures(this.pinModeLayer.createQuery())
      .then((results) => {
        if (results.features.length) {
          this.pinModeLayer.applyEdits({
            deleteFeatures: results.features,
          });
        }

        this.pinModeLayer.applyEdits({
          addFeatures: [
            new Graphic({
              geometry: new Point({
                longitude: mapPoint.longitude,
                latitude: mapPoint.latitude,
              }),
              attributes,
              symbol: this.pinModeSymbol,
            }),
          ],
        });
      })
      .finally(async () => {
        const geoLocResponse = await MapUtils.fetchGeoLocData(mapPoint);
        const xStreetsResponse = await MapUtils.fetchCrossStreets(mapPoint);
        const { streetAddress, geoLocPayload } = MapUtils.buildGeoLocPayload(
          geoLocResponse,
          xStreetsResponse
        );

        // Update state
        this.dispatch(updatePin(geoLocPayload, markerData));

        this.mapView.popup.open({
          location: mapPoint,
          content: MapUtils.getPinModePopupContent(
            this.mapView,
            streetAddress,
            this.removePinModeGraphics
          ) as any,
        });
      });
  };

  /**
   * fetchFeatureLayer
   * @param {Extent} currentExtent
   * @memberof FeatureLayerService
   */
  public fetchFeatureLayer = async (
    currentExtent: Extent,
    outages: Array<OutageViewModel>
  ): Promise<any> => {
    if (!this.streetlightLayer) {
      return Promise.resolve();
    }

    const params = new Query({
      geometry: currentExtent,
      returnGeometry: true,
      returnExceededLimitFeatures: true,
      outFields: ["*"],
      orderByFields: ["OBJECTID"],
      where: "1 = 1",
    });

    await this.queryTask
      .execute(params)
      .then((featureSet: FeatureSet) =>
        this.updateFeatureLayer(featureSet.features, outages)
      )
      .catch((error) => {
        if (error) {
          console.log(error);
        }
      })
      .finally(() => {
        return Promise.resolve();
      });
  };

  public getFeatureById = async (
    gisID: number
  ): Promise<Graphic | undefined> => {
    return await this.streetlightLayer
      .queryFeatures(this.streetlightLayer.createQuery())
      .then((results) => {
        const { features } = results;
        return features.find((feature) => {
          return feature.attributes.GISID === gisID;
        });
      });
  };

  /**
   * getStreetlightsLayer
   * @return {*}  {FeatureLayer}
   * @memberof FeatureLayerService
   */
  public getStreetlightsLayer(): FeatureLayer {
    return this.streetlightLayer;
  }

  /**
   * getPinModeLayer
   * @return {*}  {FeatureLayer}
   * @memberof FeatureLayerService
   */
  public getPinModeLayer(): FeatureLayer {
    return this.pinModeLayer;
  }

  /**
   * getRestrictedLayer
   * @return {*}  {FeatureLayer}
   * @memberof FeatureLayerService
   */
  public getRestrictedLayer(): FeatureLayer {
    return this.restrictedLayer;
  }

  /**
   * getGrandRapidsLayer
   * @return {*}  {FeatureLayer}
   * @memberof FeatureLayerService
   */
  public getGrandRapidsLayer(): FeatureLayer {
    return this.grandRapidsLayer;
  }

  /**
   * @static
   * @param {MapView} mapView
   * @param {Dispatch<Action<any>>} dispatch
   * @return {*}  {FeatureLayerService}
   * @memberof FeatureLayerService
   */
  public static getInstance(
    map: Map,
    mapView: MapView,
    dispatch: Dispatch<Action<any>>,
    appConfig: IAppConfig,
    locationWidget: LocationWidget
  ): FeatureLayerService {
    if (!FeatureLayerService.instance) {
      FeatureLayerService.instance = new FeatureLayerService(
        map,
        mapView,
        dispatch,
        appConfig,
        locationWidget
      );
    }

    return FeatureLayerService.instance;
  }
}
